import { Link } from "react-router-dom";
import SEOHead from "@/components/base/SEOHead";
import { neighborhoods } from "@/mocks/neighborhoods";
import { barrioFAQ } from "@/mocks/barrio-faq";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppFloat from "@/components/feature/WhatsAppFloat";
import FAQSection from "@/components/feature/FAQSection";
import ComoTrabajamosSection from "@/components/feature/ComoTrabajamosSection";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PricingSection from "@/components/feature/PricingSection";
import TrustBannerSection from "@/components/feature/TrustBannerSection";
import { preciosPorServicio } from "@/mocks/precios";

const averias = [
  { icon: "ri-scissors-cut-line", title: "Cinta de persiana rota", desc: "Cambiamos la cinta en el momento. Tenemos todos los anchos y colores en stock." },
  { icon: "ri-error-warning-line", title: "Persiana atascada o descolgada", desc: "Desatascamos, centramos y ajustamos los rieles para que corra suave otra vez." },
  { icon: "ri-settings-3-line", title: "Cambio de motor", desc: "Sustitución de motor averiado por uno nuevo. Trabajamos con Somfy, Nice y todas las marcas." },
  { icon: "ri-stack-line", title: "Lamas rotas o dobladas", desc: "Sustituimos solo las lamas dañadas sin necesidad de cambiar toda la persiana." },
  { icon: "ri-arrow-up-down-line", title: "Persiana que no sube o no baja", desc: "Diagnóstico y reparación: eje descentrado, muelle roto, cinta partida o motor bloqueado." },
  { icon: "ri-uninstall-line", title: "Recogedor desprendido", desc: "Reforzamos la fijación y cambiamos el recogedor si está dañado. En 20-30 minutos." },
  { icon: "ri-volume-up-line", title: "Persiana que hace ruido", desc: "Limpieza y engrase de mecanismos, ajuste de poleas y eje. Dejamos la persiana silenciosa." },
  { icon: "ri-tree-line", title: "Persiana de madera hinchada", desc: "Lijado, ajuste de holguras y tratamiento antihumedad. Especialistas en carpintería antigua." },
  { icon: "ri-remote-control-line", title: "Motorización sin obras", desc: "Convertimos tu persiana manual en motorizada. Instalación limpia en 1-2 horas. Mando incluido." },
  { icon: "ri-shield-check-line", title: "Mosquiteras a medida", desc: "Fabricación e instalación de mosquiteras enrollables, fijas y correderas. A medida exacta." },
];

export default function NeighborhoodPageContent({ slug }: { slug: string }) {
  const barrio = neighborhoods.find((n) => n.slug === slug);

  if (!barrio) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <SEOHead
          title="Barrio no encontrado | Persianas Málaga"
          description="Reparación de persianas y mosquiteras en Málaga capital. Presupuesto gratis, 2 años de garantía. Servicio en 16 barrios."
        />
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-2">Barrio no encontrado</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">Volver al inicio</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title={barrio.seoTitle}
        description={barrio.seoDescription}
      />
      <Navbar />

      <Breadcrumb items={[
        { label: "Inicio", path: "/" },
        { label: "Barrios de Málaga" },
        { label: barrio.name },
      ]} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Persianas Málaga",
        "description": `Reparación e instalación de persianas, mosquiteras y motorización en ${barrio.name}, Málaga. Técnico local especialista. Presupuesto gratis.`,
        "telephone": "+34 951 12 07 70",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Málaga",
          "addressRegion": "Andalucía",
          "addressCountry": "ES"
        },
        "areaServed": `${barrio.name}, Málaga`,
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "reviewCount": "287"
        }
      }) }} />

      {/* Hero — centré sur le problème */}
      <section className="py-14 md:py-20 bg-gradient-to-br from-background-50 via-primary-50/30 to-accent-50/30">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
            <div className="flex-1 w-full">
              <div className="inline-flex items-center gap-2 bg-primary-500/10 text-primary-700 text-xs md:text-sm font-semibold px-4 py-2 rounded-full mb-5 border border-primary-200/50">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-fill text-primary-600"></i></span>
                Técnico local en {barrio.name}
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground-950 mb-4 leading-tight font-heading">
                Reparación de persianas<br /><span className="text-primary-500">en {barrio.name}</span>
              </h1>
              <p className="text-sm md:text-lg text-foreground-600 mb-3 max-w-lg leading-relaxed">
                ¿Tu persiana no sube, no baja, se ha quedado atascada o la cinta se ha roto? ¿La mosquitera está descolgada o rota?
              </p>
              <p className="text-sm md:text-base text-foreground-500 mb-6 max-w-lg leading-relaxed">
                Técnico especialista en persianas en {barrio.name}. <strong className="text-foreground-700">Presupuesto gratis</strong>, sin coste de desplazamiento. Respuesta en 24-48 horas. <strong className="text-foreground-700">2 años de garantía</strong> por escrito.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <a
                  href="tel:+34951120770"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-background-50 font-bold text-sm md:text-base px-8 py-4 rounded-full transition-all active:scale-[0.98] whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/20"
                >
                  <span className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill"></i></span>
                  951 12 07 70
                </a>
                <a
                  href="https://wa.me/34627213347"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1faa52] text-white font-semibold text-sm md:text-base px-8 py-4 rounded-full transition-all active:scale-[0.98] whitespace-nowrap cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center"><i className="ri-whatsapp-fill"></i></span>
                  WhatsApp
                </a>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm text-foreground-600">
                <span className="flex items-center gap-1 bg-background-100 px-3 py-1.5 rounded-full">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill text-accent-400"></i></span>
                  4.9/5 — 287 valoraciones
                </span>
                <span className="flex items-center gap-1 bg-background-100 px-3 py-1.5 rounded-full">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line text-primary-500"></i></span>
                  Respuesta 24/48h
                </span>
                <span className="flex items-center gap-1 bg-background-100 px-3 py-1.5 rounded-full">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-shield-check-line text-accent-500"></i></span>
                  2 años de garantía
                </span>
              </div>
            </div>
            <div className="w-full lg:w-[45%] h-64 sm:h-80 md:h-96 rounded-2xl overflow-hidden flex-shrink-0">
              <img
                alt={`Técnico especialista reparando una persiana en ${barrio.name}, Málaga — servicio profesional de persianas y mosquiteras`}
                className="w-full h-full object-cover object-top"
                loading="eager"
                src={barrio.imageUrl}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ⭐ SECTION CLÉ : Averías que resolvemos */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 font-heading">
              Averías de persianas que resolvemos en {barrio.name}
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              Si reconoces tu problema en esta lista, llámanos. Tenemos la solución.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-4">
            {averias.map((item, i) => (
              <div key={i} className="bg-background-50 rounded-xl p-4 md:p-5 border border-background-200/60 hover:border-primary-200/60 transition-colors">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-600 mb-3">
                  <span className="w-5 h-5 flex items-center justify-center"><i className={item.icon}></i></span>
                </div>
                <h3 className="text-sm font-bold text-foreground-900 mb-1.5">{item.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conocemos tu barrio — UN SEUL paragraphe utile, pas touristique */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="px-4 md:px-6 max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-5 font-heading text-center">
            Conocemos las viviendas de {barrio.name} y sus problemas
          </h2>
          <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/60">
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              {barrio.localText}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row justify-center gap-3 mt-7">
            <a
              href="tel:+34951120770"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-background-50 font-bold text-sm px-8 py-4 rounded-full transition-all active:scale-[0.98] whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/20"
            >
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill"></i></span>
              Llama ahora — 951 12 07 70
            </a>
          </div>
        </div>
      </section>

      {/* Rassurance */}
      <section className="py-10 md:py-14 bg-gradient-to-br from-background-50 via-primary-50/15 to-accent-50/20">
        <div className="px-4 md:px-6 max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 font-heading">
              Tranquilidad garantizada al reparar en {barrio.name}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            <div className="bg-background-50 rounded-2xl p-5 md:p-6 border border-background-200/60 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500/10 text-primary-600 mx-auto mb-3">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-timer-flash-line text-xl"></i></span>
              </div>
              <h3 className="text-sm font-bold text-foreground-900 mb-1.5">Respuesta 24/48h</h3>
              <p className="text-xs text-foreground-500">Para urgencias, podemos estar el mismo día si llamas antes de las 12:00.</p>
            </div>
            <div className="bg-background-50 rounded-2xl p-5 md:p-6 border border-background-200/60 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-400/10 text-accent-500 mx-auto mb-3">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-car-line text-xl"></i></span>
              </div>
              <h3 className="text-sm font-bold text-foreground-900 mb-1.5">Sin coste de desplazamiento</h3>
              <p className="text-xs text-foreground-500">Venimos a {barrio.name} sin cobrar el viaje. Presupuesto gratis y sin compromiso.</p>
            </div>
            <div className="bg-background-50 rounded-2xl p-5 md:p-6 border border-background-200/60 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-500/10 text-secondary-600 mx-auto mb-3">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-shield-check-line text-xl"></i></span>
              </div>
              <h3 className="text-sm font-bold text-foreground-900 mb-1.5">2 años de garantía</h3>
              <p className="text-xs text-foreground-500">Por escrito. Cubre piezas y mano de obra. Si falla, volvemos sin coste.</p>
            </div>
            <div className="bg-background-50 rounded-2xl p-5 md:p-6 border border-background-200/60 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500/10 text-primary-600 mx-auto mb-3">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-check-double-line text-xl"></i></span>
              </div>
              <h3 className="text-sm font-bold text-foreground-900 mb-1.5">Todas las marcas</h3>
              <p className="text-xs text-foreground-500">Somfy, Nice, Gaviota, Gradhermetic, Simbac... reparamos cualquier marca.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection
        title={`Preguntas frecuentes sobre reparación de persianas en ${barrio.name}`}
        questions={[
          {
            question: `¿Cuánto tardáis en llegar a ${barrio.name}?`,
            answer: `Nuestro tiempo medio de respuesta en ${barrio.name} es de 24 a 48 horas. Para urgencias (persiana bloqueada que no cierra), podemos estar en el mismo día si nos llamas antes de las 12:00. No cobramos desplazamiento en ningún barrio de Málaga capital.`,
          },
          {
            question: "¿El presupuesto es gratis y sin compromiso?",
            answer: "Sí. El presupuesto es totalmente gratis y sin compromiso. Te damos un precio cerrado por escrito antes de empezar cualquier trabajo, sin sorpresas. Puedes pedirlo por teléfono, WhatsApp o formulario web.",
          },
          {
            question: "¿Qué garantía tienen las reparaciones?",
            answer: "Todas nuestras reparaciones e instalaciones cuentan con 2 años de garantía por escrito, cubriendo tanto las piezas sustituidas como la mano de obra. Si algo falla dentro de ese periodo, lo arreglamos sin coste adicional.",
          },
          {
            question: "¿Es mejor reparar o cambiar mi persiana?",
            answer: "En la mayoría de los casos, reparar sale más rentable. Una persiana con cinta rota, recogedor dañado o motor averiado se repara perfectamente. Solo recomendamos cambio completo cuando la persiana tiene más de 25 años o los daños son tan extensos que reparar pieza por pieza no compensa. Te lo decimos con honestidad en el diagnóstico gratuito.",
          },
          ...(barrioFAQ[slug] || []),
        ]}
      />

      {/* Servicios disponibles */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-8 font-heading text-center">
            Todos nuestros servicios disponibles en {barrio.name}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            <Link to="/servicios/persianas" className="group p-5 md:p-6 bg-background-100 rounded-2xl border border-background-200/60 hover:border-primary-300/60 transition-all hover:-translate-y-0.5 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 text-white mb-3">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-window-line text-lg"></i></span>
              </div>
              <h3 className="text-sm md:text-base font-bold text-foreground-900 mb-1">Persianas</h3>
              <p className="text-xs text-foreground-500">Reparación, cambio de cintas, lamas, motores, motorización</p>
            </Link>
            <Link to="/servicios/mosquiteras" className="group p-5 md:p-6 bg-background-100 rounded-2xl border border-background-200/60 hover:border-secondary-300/60 transition-all hover:-translate-y-0.5 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-secondary-500 to-secondary-600 text-white mb-3">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-shield-check-line text-lg"></i></span>
              </div>
              <h3 className="text-sm md:text-base font-bold text-foreground-900 mb-1">Mosquiteras</h3>
              <p className="text-xs text-foreground-500">A medida, enrollables, fijas, correderas y plisadas</p>
            </Link>
            <Link to="/servicios/motorizacion" className="group p-5 md:p-6 bg-background-100 rounded-2xl border border-background-200/60 hover:border-primary-300/60 transition-all hover:-translate-y-0.5 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-secondary-900 to-secondary-800 text-white mb-3">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-remote-control-line text-lg"></i></span>
              </div>
              <h3 className="text-sm md:text-base font-bold text-foreground-900 mb-1">Motorización</h3>
              <p className="text-xs text-foreground-500">Motores Somfy y Nice, automatización sin obras, domótica</p>
            </Link>
          </div>
        </div>
      </section>

      <PricingSection ids={[...preciosPorServicio["persianas"].slice(0, 5), ...preciosPorServicio["mosquiteras"].slice(0, 1)]} className="py-10 md:py-14 bg-background-100" />

      <ComoTrabajamosSection />

      {/* Guías relacionadas */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="px-4 md:px-6 max-w-4xl mx-auto text-center">
          <h3 className="text-lg md:text-xl font-bold text-foreground-950 mb-6 font-heading">Guías útiles de reparación de persianas</h3>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/guias/como-cambiar-cinta-persiana" className="text-sm text-primary-600 hover:text-primary-700 underline cursor-pointer">Cómo cambiar la cinta</Link>
            <span className="text-foreground-300 select-none">|</span>
            <Link to="/guias/persiana-no-sube-ni-baja" className="text-sm text-primary-600 hover:text-primary-700 underline cursor-pointer">No sube ni baja</Link>
            <span className="text-foreground-300 select-none">|</span>
            <Link to="/guias/persiana-atascada-descolgada" className="text-sm text-primary-600 hover:text-primary-700 underline cursor-pointer">Atascada o descolgada</Link>
            <span className="text-foreground-300 select-none">|</span>
            <Link to="/guias/cambiar-motor-persiana" className="text-sm text-primary-600 hover:text-primary-700 underline cursor-pointer">Cambiar el motor</Link>
            <span className="text-foreground-300 select-none">|</span>
            <Link to="/guias/cambiar-lamas-persiana" className="text-sm text-primary-600 hover:text-primary-700 underline cursor-pointer">Cambiar lamas rotas</Link>
            <span className="text-foreground-300 select-none">|</span>
            <Link to="/guias/reparar-recogedor-persiana" className="text-sm text-primary-600 hover:text-primary-700 underline cursor-pointer">Reparar el recogedor</Link>
            <span className="text-foreground-300 select-none">|</span>
            <Link to="/guias" className="text-sm text-primary-600 hover:text-primary-700 underline cursor-pointer font-semibold">Ver todas →</Link>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="py-10 md:py-14 bg-gradient-to-r from-primary-500 to-primary-600">
        <div className="px-4 md:px-6 max-w-4xl mx-auto text-center">
          <h3 className="text-xl md:text-2xl font-bold text-background-50 mb-3 font-heading">
            ¿Vives en {barrio.name}? Pide tu presupuesto gratis ahora
          </h3>
          <p className="text-sm md:text-base text-background-50/80 mb-6">
            Sin coste de desplazamiento. Respuesta en menos de 15 minutos. Precio cerrado antes de empezar.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <a
              href="tel:+34951120770"
              className="inline-flex items-center justify-center gap-2 bg-background-50 hover:bg-background-100 text-primary-600 font-semibold text-sm px-8 py-4 rounded-full transition-all whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill text-base"></i></span>
              951 12 07 70
            </a>
            <a
              href="https://wa.me/34627213347"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1faa52] text-white font-semibold text-sm px-8 py-4 rounded-full transition-all whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-whatsapp-fill text-base"></i></span>
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Otros barrios — maillage interne SEO */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <h3 className="text-lg md:text-xl font-bold text-foreground-950 mb-6 text-center font-heading">
            También reparamos persianas en otros barrios de Málaga
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-8 gap-2">
            {neighborhoods.filter(n => n.slug !== slug).map((n) => (
              <Link
                key={n.slug}
                to={`/barrios/${n.slug}`}
                className="text-xs md:text-sm text-foreground-600 hover:text-primary-500 transition-colors py-1.5 px-2 text-center cursor-pointer"
              >
                {n.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustBannerSection />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}